import { Button, HStack, ListItem, Text, UnorderedList, VStack } from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailField } from '../../../components/DetailField'
import { FormModal } from '../../../components/FormModal'
import { ListInput } from '../../../components/ListInput'
import { LoadingBlock } from '../../../components/Loading'
import { ReasonModal } from '../../../components/ReasonModal'
import { StatusBadge } from '../../../components/StatusBadge'
import { useUser } from '../../../contexts/UserContext'
import { formatApiDate, formatCurrency, formatDateTime, useAppToast, useHasRole } from '../../../helpers'
import { JobRequisitionService } from '../../../services/JobRequisition/JobRequisitionService'
import { JobRequisitionDetail } from '../../../services/JobRequisition/types'
import { LIST_FIELD_LABELS, LIST_FIELDS } from '../JobPositions/constants'
import {
  CONTRACT_TYPE_LABELS,
  HISTORY_EVENT_LABELS,
  JOB_REQUISITION_STATUS,
  JOB_REQUISITION_TAB_DEFS,
  REASON_MAX_LENGTH
} from './constants'
import { businessDaysLabel, DescriptiveNotice, RequisitionSourceFields, sourceFromDetail } from './JobRequisitionParts'
import { JobRequisitionScheduleEditor } from './JobRequisitionScheduleEditor'

interface IJobRequisitionViewModalProps {
  isOpen: boolean
  onClose: () => void
  /** The RP to show; null while none is chosen, so the modal can stay mounted. */
  requisitionId: number | null
  /** Called after the signer signs or rejects the RP. */
  onChanged: () => void
}

/**
 * The whole RP, read-only, with its history. The chosen signer analyses it here and signs or
 * rejects it — the bell and the e-mail link straight to this view.
 */
export function JobRequisitionViewModal({ isOpen, onClose, requisitionId, onChanged }: IJobRequisitionViewModalProps) {
  const service = new JobRequisitionService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { id: userId } = useUser()
  const [detail, setDetail] = useState<JobRequisitionDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [isSignDialogOpen, setIsSignDialogOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)

  useEffect(() => {
    if (!isOpen || requisitionId === null) return

    setDetail(null)
    setTabIndex(0)
    setLoading(true)
    service
      .show(requisitionId)
      .then(setDetail)
      .catch((error) => {
        toast.error('Erro ao carregar a RP', error)
        onClose()
      })
      .finally(() => setLoading(false))
  }, [isOpen, requisitionId])

  const canDecide =
    detail !== null &&
    detail.status === 'pending_validation' &&
    hasRole('sign_job_requisitions') &&
    Number(userId) === detail.signer_user_id

  const afterDecision = (updated: JobRequisitionDetail) => {
    setDetail(updated)
    onChanged()
  }

  const handleSign = () => {
    if (!detail) return Promise.resolve()

    return service
      .sign(detail.id)
      .then((updated) => {
        toast.success(`RP ${updated.number} assinada`, 'A vaga está em aberto.')
        setIsSignDialogOpen(false)
        afterDecision(updated)
      })
      .catch((error) => toast.error('Erro ao assinar a RP', error))
  }

  const handleReject = (reason: string) => {
    if (!detail) return Promise.resolve()

    return service
      .reject(detail.id, reason)
      .then((updated) => {
        toast.success(`RP ${updated.number} reprovada`)
        afterDecision(updated)
      })
      .catch((error) => {
        toast.error('Erro ao reprovar a RP', error)
        throw error
      })
  }

  const panel = (content: ReactNode) =>
    loading || !detail ? (
      <LoadingBlock entity="RP" />
    ) : (
      <VStack align="stretch" spacing={5}>
        {content}
      </VStack>
    )

  const source = detail ? sourceFromDetail(detail) : null

  const mpTab = detail && source && (
    <>
      <DetailField label="Status">
        <StatusBadge status={detail.status} statuses={JOB_REQUISITION_STATUS} />
      </DetailField>
      <DetailField label="MP" value={source.mpLabel} />
      <RequisitionSourceFields source={source} />
      <DetailField label="Gestor Imediato" value={detail.manager_name} />
      <DetailField label="Data de Abertura" value={formatApiDate(detail.opened_at)} />
      <DetailField label="Prazo de Fechamento" value={formatApiDate(detail.deadline)} />
      <DetailField label="Data de Fechamento" value={formatApiDate(detail.closed_at)} />
      <DetailField
        label="Tempo de Fechamento"
        value={detail.closing_business_days !== null ? businessDaysLabel(detail.closing_business_days) : null}
      />
    </>
  )

  const descriptiveTab = detail && source && (
    <>
      <DescriptiveNotice source={source} />
      {LIST_FIELDS.map((field) => (
        <DetailField key={field} label={LIST_FIELD_LABELS[field]}>
          <ListInput value={detail[field]} onChange={() => undefined} isReadOnly />
        </DetailField>
      ))}
    </>
  )

  const scheduleTab = detail && (
    <>
      <DetailField label="Regime de Contratação" value={CONTRACT_TYPE_LABELS[detail.contract_type]} />
      <DetailField label="Salário" value={formatCurrency(detail.salary)} />
      <DetailField label="Dias e horários de trabalho">
        <JobRequisitionScheduleEditor value={detail.schedule} onChange={() => undefined} fieldErrors={{}} isReadOnly />
      </DetailField>
    </>
  )

  const siteTab = detail && (
    <>
      <DetailField label="Áreas" value={detail.areas?.length ? detail.areas.join(', ') : null} />
      <DetailField label="Permitir envio de Portfólio" value={detail.allow_portfolio_link ? 'Sim' : 'Não'} />
      <DetailField label="Assinante" value={detail.signer.name} />
    </>
  )

  const historyTab = detail && (
    <UnorderedList spacing={3} pl={1}>
      {detail.history.map((event) => (
        <ListItem key={event.event}>
          <Text>
            <strong>{HISTORY_EVENT_LABELS[event.event]}</strong>
            {event.by ? ` por ${event.by}` : ''} em{' '}
            {event.event === 'closed' ? formatApiDate(event.at) : formatDateTime(event.at)}
          </Text>
          {event.note && (
            <Text variant="caption" whiteSpace="pre-wrap">
              Motivo: {event.note}
            </Text>
          )}
        </ListItem>
      ))}
    </UnorderedList>
  )

  const footer = canDecide ? (
    <HStack spacing={2}>
      <Button variant="ghost" onClick={onClose}>
        Fechar
      </Button>
      <ActionButton action="reject" label="Reprovar" showLabel mr={0} onClick={() => setIsRejectModalOpen(true)} />
      <ActionButton action="sign" label="Assinar RP" showLabel mr={0} onClick={() => setIsSignDialogOpen(true)} />
    </HStack>
  ) : undefined

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={detail ? `RP ${detail.number} · ${detail.job_position.name}` : 'RP'}
        size="editor"
        footer={footer}
        tabIndex={tabIndex}
        onTabChange={setTabIndex}
        tabs={[
          ...JOB_REQUISITION_TAB_DEFS.map((tab, index) => ({
            label: tab.label,
            content: panel([mpTab, descriptiveTab, scheduleTab, siteTab][index])
          })),
          { label: 'Histórico', content: panel(historyTab) }
        ]}
      />

      {detail && (
        <>
          <ConfirmDialog
            isOpen={isSignDialogOpen}
            onClose={() => setIsSignDialogOpen(false)}
            onConfirm={handleSign}
            action="sign"
            title={`Assinar RP ${detail.number}`}
            description={
              <>
                Ao assinar, a vaga de <strong>{detail.job_position.name}</strong> fica em aberto, com prazo de 20 dias
                úteis para o fechamento.
              </>
            }
          />
          <ReasonModal
            isOpen={isRejectModalOpen}
            onClose={() => setIsRejectModalOpen(false)}
            title={`Reprovar RP ${detail.number}`}
            label="Motivo da reprovação"
            description="A RP reprovada não volta: para seguir com a vaga, o RH abre uma nova RP a partir da MP. Quem criou a RP recebe o motivo."
            confirmLabel="Reprovar"
            maxLength={REASON_MAX_LENGTH}
            onConfirm={handleReject}
          />
        </>
      )}
    </>
  )
}
